import React, { useEffect, useMemo, useState } from 'react';
import { EventLog } from '../types';
import { resolveIPs, isPublicIP, GeoIPResult } from '../services/geoip';
import { Loader2, MapPin } from 'lucide-react';

const MAP_W = 800;
const MAP_H = 450;
const WORLD_IMG = 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Equirectangular-projection.jpg/1280px-Equirectangular-projection.jpg';

function toXY(lat: number, lng: number): [number, number] {
  const x = ((lng + 180) / 360) * MAP_W;
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = MAP_H / 2 - (mercN / Math.PI) * (MAP_H / 2);
  return [Math.max(0, Math.min(MAP_W, x)), Math.max(0, Math.min(MAP_H, y))];
}

interface IPActivity {
  ip: string;
  eventCount: number;
  lastSeen: string; // ISO timestamp of most recent event from this IP
}

interface Props {
  events: EventLog[];
  accentColor?: string;
}

/**
 * Shows where a single user has been active, based on the IP addresses
 * attached to their event log. The most recently used location is
 * highlighted; older locations render as smaller, dimmer pins.
 */
export default function UserLocationMap({ events, accentColor = '#f97316' }: Props) {
  const [geoData, setGeoData] = useState<Map<string, GeoIPResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [hoveredIP, setHoveredIP] = useState<string | null>(null);

  // Aggregate the user's events into distinct public IPs
  const ipActivity = useMemo<IPActivity[]>(() => {
    const byIP = new Map<string, IPActivity>();
    events.forEach((e) => {
      if (!isPublicIP(e.ip_address)) return;
      const ip = e.ip_address;
      const existing = byIP.get(ip);
      if (existing) {
        existing.eventCount += 1;
        if (e.created_at > existing.lastSeen) existing.lastSeen = e.created_at;
      } else {
        byIP.set(ip, { ip, eventCount: 1, lastSeen: e.created_at });
      }
    });
    // Most recently seen first
    return Array.from(byIP.values()).sort((a, b) => b.lastSeen.localeCompare(a.lastSeen));
  }, [events]);

  useEffect(() => {
    if (ipActivity.length === 0) return;
    let cancelled = false;
    const resolve = async () => {
      setLoading(true);
      const results = await resolveIPs(
        ipActivity.map((a) => a.ip),
        (partial) => { if (!cancelled) setGeoData(new Map(partial)); }
      );
      if (!cancelled) {
        setGeoData(results);
        setLoading(false);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [ipActivity]);

  const markers = useMemo(() => {
    return ipActivity
      .filter((a) => geoData.has(a.ip))
      .map((a, idx) => {
        const geo = geoData.get(a.ip)!;
        const [x, y] = toXY(geo.lat, geo.lng);
        return { ...a, geo, x, y, isLatest: idx === 0 };
      });
  }, [ipActivity, geoData]);

  // Nothing to show at all (e.g. no events with a public IP)
  if (ipActivity.length === 0) return null;

  const latest = markers.find((m) => m.isLatest);
  const hovered = hoveredIP ? markers.find((m) => m.ip === hoveredIP) : null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-white flex items-center gap-2">
            <MapPin size={16} className="text-orange-400" />
            Location
          </h4>
          {latest ? (
            <p className="text-sm text-slate-500 mt-0.5">
              Last seen in <span className="text-slate-300">{latest.geo.city}, {latest.geo.country}</span>
              {' · '}{new Date(latest.lastSeen).toLocaleString()}
            </p>
          ) : (
            <p className="text-sm text-slate-500 mt-0.5">
              {loading ? 'Resolving IP locations…' : 'No geo data available for this user\u2019s IPs'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
            Latest
          </span>
          {markers.length > 1 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-500" />
              Previous
            </span>
          )}
          {loading && <Loader2 size={12} className="animate-spin text-slate-500" />}
        </div>
      </div>

      {markers.length > 0 && (
        <div className="relative">
          <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="w-full" style={{ backgroundColor: '#0f172a' }}>
            <image
              href={WORLD_IMG}
              x="0" y="0"
              width={MAP_W} height={MAP_H}
              opacity="0.15"
              preserveAspectRatio="xMidYMid slice"
            />

            {/* Older pins first so the latest renders on top */}
            {[...markers].reverse().map((m) => {
              const color = m.isLatest ? accentColor : '#64748b';
              const r = m.isLatest ? 7 : 4.5;
              const isHovered = hoveredIP === m.ip;
              return (
                <g
                  key={m.ip}
                  onMouseEnter={() => setHoveredIP(m.ip)}
                  onMouseLeave={() => setHoveredIP(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx={m.x} cy={m.y} r={r + 6} fill={color} fillOpacity={0.1} />
                  <circle cx={m.x} cy={m.y} r={r + 2} fill={color} fillOpacity={isHovered ? 0.4 : 0.2} />
                  <circle cx={m.x} cy={m.y} r={r} fill={color} fillOpacity={0.9} />
                  {isHovered && (
                    <circle cx={m.x} cy={m.y} r={r + 8} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.6} />
                  )}
                </g>
              );
            })}
          </svg>

          {hovered && (
            <div className="absolute top-3 right-3 bg-black/90 border border-slate-700 rounded-xl px-4 py-3 text-xs space-y-1 pointer-events-none z-10 min-w-[180px]">
              <div className="text-slate-400 text-sm">{hovered.geo.city}, {hovered.geo.country}</div>
              <div className="font-mono text-slate-500">{hovered.ip}</div>
              <div className="flex items-center gap-3 pt-1 border-t border-slate-800 mt-1">
                <span className="text-slate-500">{hovered.eventCount} event{hovered.eventCount === 1 ? '' : 's'}</span>
                <span className="text-slate-500">Last seen {new Date(hovered.lastSeen).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
